import { ref } from 'vue'
import apiInstance from '@/utils/api'

export interface LocalizedFoodCategoryContent {
  [language: string]: {
    name: string
  }
}

export interface FoodCategory {
  id: number | string
  // The restaurant's Deposit (Pfand) category: its items take the VAT of the item they are an option of.
  is_deposit?: boolean
  localizedContent: LocalizedFoodCategoryContent
}

export interface FoodCategoryResponse {
  error: boolean
  message: string
  data: FoodCategory[] | FoodCategory
}

export const useFoodCategories = (getRestaurantId?: () => string) => {
  const categories = ref<FoodCategory[]>([])
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  /**
   * Fetch food categories from backend
   */
  const fetchFoodCategories = async (): Promise<void> => {
    if (!getRestaurantId) {
      console.warn('Restaurant ID getter not provided')
      return
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to fetch food categories'
      return
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.get<FoodCategoryResponse>(
        `/restaurant/${restaurantId}/food-categories`
      )

      if (response.data.error === false && Array.isArray(response.data.data)) {
        categories.value = response.data.data
      } else {
        error.value = response.data.message || 'Failed to fetch food categories'
        categories.value = []
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to fetch food categories'
      categories.value = []
      console.error('Error fetching food categories:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Create or update a food category
   * For create: omit category_id
   * For update: include category_id
   * Only includes languages that have non-empty names
   */
  const saveCategory = async (localizedContent: LocalizedFoodCategoryContent, categoryId?: number | string): Promise<{ success: boolean; data?: FoodCategory; errors?: Record<string, string[]> }> => {
    if (!getRestaurantId) {
      error.value = 'Restaurant ID getter not provided'
      return { success: false, errors: { general: ['Restaurant ID not provided'] } }
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required'
      return { success: false, errors: { general: ['Restaurant ID is required'] } }
    }

    isLoading.value = true
    error.value = null

    try {
      // Filter out languages with empty names
      const filteredContent: LocalizedFoodCategoryContent = {}
      Object.keys(localizedContent).forEach(lang => {
        if (localizedContent[lang]?.name?.trim()) {
          filteredContent[lang] = localizedContent[lang]
        }
      })

      const payload: any = { localizedContent: filteredContent }
      if (categoryId) {
        payload.category_id = categoryId
      }

      const response = await apiInstance.put<FoodCategoryResponse>(
        `/restaurant/${restaurantId}/food-categories`,
        payload
      )

      if (response.data.error === false && response.data.data) {
        const resultCategory = response.data.data as FoodCategory

        if (categoryId) {
          // Update existing
          const index = categories.value.findIndex(c => c.id === categoryId)
          if (index !== -1) {
            categories.value[index] = resultCategory
          }
        } else {
          // Create new
          categories.value.push(resultCategory)
        }

        return { success: true, data: resultCategory }
      } else {
        // Handle validation errors
        const validationErrors = (response.data as any)?.errors || {}
        error.value = response.data.message || 'Failed to save food category'
        return { success: false, errors: validationErrors }
      }
    } catch (err: any) {
      const validationErrors = err?.response?.data?.errors || {}
      error.value = err?.response?.data?.message || err?.message || 'Failed to save food category'
      console.error('Error saving food category:', err)
      return { success: false, errors: validationErrors }
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Create a new food category
   */
  const createCategory = async (localizedContent: LocalizedFoodCategoryContent): Promise<FoodCategory | null> => {
    const result = await saveCategory(localizedContent)
    return result.data || null
  }

  /**
   * Update an existing food category
   */
  const updateCategory = async (id: number | string, localizedContent: LocalizedFoodCategoryContent): Promise<boolean> => {
    const result = await saveCategory(localizedContent, id)
    return result.success
  }

  /**
   * Delete a food category
   */
  const deleteCategory = async (id: number | string): Promise<boolean> => {
    if (!getRestaurantId) {
      error.value = 'Restaurant ID getter not provided'
      return false
    }

    const restaurantId = getRestaurantId()
    if (!restaurantId) {
      error.value = 'Restaurant ID is required to delete food category'
      return false
    }

    isLoading.value = true
    error.value = null

    try {
      const response = await apiInstance.delete<FoodCategoryResponse>(
        `/restaurant/${restaurantId}/food-categories/${id}`
      )

      if (response.data.error === false) {
        categories.value = categories.value.filter(c => c.id !== id)
        return true
      } else {
        error.value = response.data.message || 'Failed to delete food category'
        return false
      }
    } catch (err: any) {
      error.value = err?.response?.data?.message || err?.message || 'Failed to delete food category'
      console.error('Error deleting food category:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Reset the module state
   */
  const reset = () => {
    categories.value = []
    isLoading.value = false
    error.value = null
  }

  return {
    categories,
    isLoading,
    error,
    fetchFoodCategories,
    saveCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    reset,
  }
}
